import { enrollActorIdentity, unlockActorIdentity } from "@/lib/captainMesh/transport/identity";
import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowLeft, LockKeyhole, ShieldCheck, Delete, KeyRound } from "lucide-react";
import { usePOS } from "@/context/POSContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { isRemote } from "@/lib/remote";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/**
 * Say which of the three things went wrong.
 */
export const loginErrorFor = err => {
  const response = err?.response;
  if (!response || [502, 503, 504].includes(response.status)) return "Local server is unavailable";
  if (response.status === 423) {
    return response.data?.detail || "Too many wrong attempts. Account is locked. A manager can unlock it.";
  }
  if (response.status >= 500) return "The server had a problem signing you in. Your PIN was not checked. Try again in a moment.";
  if (response.status === 429) {
    return response.data?.detail || response.data?.error || "Too many attempts — wait a minute and try again.";
  }
  const detail = response.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) return detail[0]?.msg || "Sign-in was rejected";
  return response.data?.error || "Incorrect username or PIN/password";
};

export default function LoginPage() {
  const remote = isRemote();
  // Two login screens (owner, 2026-11-03):
  // 1. "pin" screen in front — staff enter their unique PIN directly on keypad.
  // 2. "credentials" screen — for owner, admin, or staff signing in with ID & password.
  const [mode, setMode] = useState(remote ? "credentials" : "pin");
  const [employeeId, setEmployeeId] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { login } = usePOS();
  const navigate = useNavigate();
  const location = useLocation();

  const executeLogin = useCallback(async (empId, secret) => {
    if (!secret) return;
    setError("");
    setLoading(true);
    try {
      const payload = empId ? { employee_id: empId, pin: secret } : { pin: secret };
      const { data } = await axios.post(`${API}/auth/login`, payload, { withCredentials: true });
      if (remote) {
        login({ ...data.user, landing: data.landing, permissions_version: data.permissions_version }, data.access_token, data.permissions);
        navigate(data.landing || "/floor");
        return;
      }
      const actualEmpId = data.user?.employee_id || empId;
      try {
        await enrollActorIdentity(actualEmpId, secret, data.access_token);
      } catch (enrollError) {
        console.warn("Mesh identity enrollment skipped:", enrollError?.message);
        const { setActiveActorSession, getDeviceId } = await import("@/lib/captainMesh/transport/identity");
        const deviceId = await getDeviceId();
        setActiveActorSession({
          employee_id: actualEmpId,
          privateKey: "dummy_private",
          authorization: { device_id: deviceId, role: data.user?.role || "captain", user_id: data.user?.id || 1 },
          device_id: deviceId
        });
      }
      login({ ...data.user, landing: data.landing, permissions_version: data.permissions_version }, data.access_token, data.permissions);
      const from = location.state?.from;
      navigate(from && !from.startsWith("/login") ? from : (data.landing || "/floor"));
    } catch (err) {
      if (!err.response && !remote && empId) {
        const authRecord = await unlockActorIdentity(empId, secret);
        if (authRecord) {
          const offlineUser = {
            id: authRecord.user_id,
            employee_id: empId,
            role: authRecord.role,
            name: empId
          };
          login(offlineUser, "offline-token", null);
          navigate("/floor");
          return;
        }
      }
      setError(loginErrorFor(err));
      setPin("");
    } finally {
      setLoading(false);
    }
  }, [location.state?.from, login, navigate, remote]);

  // Keypad actions for the PIN screen
  const handleKeypadPress = useCallback((val) => {
    if (loading) return;
    if (val === "clear") {
      setPin("");
      setError("");
      return;
    }
    if (val === "backspace") {
      setPin(prev => prev.slice(0, -1));
      setError("");
      return;
    }
    if (pin.length >= 10) return;
    const nextPin = pin + val;
    setPin(nextPin);
    setError("");

    // Auto-submit when 4 digits are entered
    if (nextPin.length === 4) {
      executeLogin(null, nextPin);
    }
  }, [loading, pin, executeLogin]);

  // Keyboard support for numeric keypad on PC
  useEffect(() => {
    if (mode !== "pin") return;
    const handleKeyDown = (e) => {
      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        handleKeypadPress(e.key);
      } else if (e.key === "Backspace") {
        e.preventDefault();
        handleKeypadPress("backspace");
      } else if (e.key === "Escape") {
        handleKeypadPress("clear");
      } else if (e.key === "Enter" && pin.length >= 4) {
        executeLogin(null, pin);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeypadPress, mode, pin, executeLogin]);

  const submitManual = (e) => {
    e.preventDefault();
    executeLogin(employeeId, pin);
  };

  return (
    <main className="login-page" data-testid="login-page">
      {/* Brand panel */}
      <section className="login-brand-panel" data-testid="login-brand-panel">
        <div className="login-logo">
          <span>RY</span>
          RestoYaar
        </div>
        <div className="login-message">
          <div className="status-pill">
            <ShieldCheck size={14} />
            Local-first · Data stays on premises
          </div>
          <h1>Service moves fast.<br />Your POS should too.</h1>
          <p>One operating system for the floor, counter, kitchen and back office.</p>
        </div>
        <div className="login-metrics">
          <span data-testid="login-secure-note"><LockKeyhole size={13} /> PINs never stored or prefilled</span>
        </div>
      </section>

      {/* Form panel */}
      <section className="login-form-panel" data-testid="login-form-panel">
        <div className="login-form">
          {/* SCREEN 1: QUICK PIN SCREEN (Front / Default for staff) */}
          {mode === "pin" && !remote && (
            <div className="space-y-4 animate-in fade-in duration-150" data-testid="login-pin-mode">
              <div className="text-center sm:text-left">
                <div className="eyebrow">Staff Access · त्वरित हाज़िरी</div>
                <h2>Quick PIN Sign-In</h2>
                <p>Enter your unique 4-digit PIN to sign in and record shift duty.</p>
              </div>

              {/* PIN Mask Dots */}
              <div className="flex flex-col items-center gap-2.5 my-2">
                <div className="flex items-center gap-4 py-2" data-testid="login-pin-dots">
                  {[0, 1, 2, 3].map(idx => (
                    <div
                      key={idx}
                      className={`w-4 h-4 rounded-full border-2 transition-all duration-150 ${
                        pin.length > idx
                          ? "bg-primary border-primary scale-125 shadow-xs"
                          : "border-muted-foreground/30 bg-muted/20"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {error && <div className="form-error" data-testid="login-error-message">{error}</div>}

              {/* Numeric Keypad */}
              <div className="grid grid-cols-3 gap-2.5 max-w-[280px] mx-auto" data-testid="login-numeric-keypad">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map(digit => (
                  <button
                    key={digit}
                    type="button"
                    onClick={() => handleKeypadPress(digit)}
                    disabled={loading}
                    data-testid={`keypad-button-${digit}`}
                    className="h-14 sm:h-16 rounded-2xl border border-border bg-card text-foreground font-semibold text-2xl hover:bg-muted/70 active:scale-95 transition-all shadow-xs flex items-center justify-center disabled:opacity-50"
                  >
                    {digit}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => handleKeypadPress("clear")}
                  disabled={loading || pin.length === 0}
                  data-testid="keypad-button-clear"
                  className="h-14 sm:h-16 rounded-2xl border border-border bg-card/60 text-muted-foreground font-medium text-xs hover:bg-muted active:scale-95 transition-all flex items-center justify-center disabled:opacity-40"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => handleKeypadPress("0")}
                  disabled={loading}
                  data-testid="keypad-button-0"
                  className="h-14 sm:h-16 rounded-2xl border border-border bg-card text-foreground font-semibold text-2xl hover:bg-muted/70 active:scale-95 transition-all shadow-xs flex items-center justify-center disabled:opacity-50"
                >
                  0
                </button>
                <button
                  type="button"
                  onClick={() => handleKeypadPress("backspace")}
                  disabled={loading || pin.length === 0}
                  data-testid="keypad-button-backspace"
                  aria-label="Backspace"
                  className="h-14 sm:h-16 rounded-2xl border border-border bg-card/60 text-muted-foreground hover:bg-muted active:scale-95 transition-all flex items-center justify-center disabled:opacity-40"
                >
                  <Delete className="w-5 h-5" />
                </button>
              </div>

              {/* Manual submit button if 4+ digits */}
              {pin.length >= 4 && (
                <Button
                  variant="action"
                  type="button"
                  onClick={() => executeLogin(null, pin)}
                  className="w-full h-11 login-submit mt-2"
                  disabled={loading}
                  data-testid="login-pin-submit"
                >
                  {loading ? "Checking PIN…" : "Enter RestoYaar"}
                  <ArrowRight size={18} />
                </Button>
              )}

              {/* Switch to Screen 2: Username & Password */}
              <div className="pt-4 border-t border-border mt-3 text-center">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => { setMode("credentials"); setError(""); setPin(""); }}
                  data-testid="switch-to-id-pass"
                  className="w-full h-11 border-border text-foreground hover:bg-muted flex items-center justify-center gap-2 text-xs sm:text-sm font-medium"
                >
                  <KeyRound className="w-4 h-4 text-primary" />
                  <span>Use ID / Password Instead</span>
                </Button>
                <p className="text-[11px] text-muted-foreground mt-2">
                  For owners, admins, or anyone using username & password
                </p>
              </div>
            </div>
          )}

          {/* SCREEN 2: USERNAME / ID & PASSWORD SCREEN */}
          {(mode === "credentials" || remote) && (
            <div className="space-y-4 animate-in fade-in duration-150" data-testid="login-credentials-mode">
              <div>
                <div className="eyebrow">Staff Access · खाता लॉगिन</div>
                <h2>Welcome back</h2>
                <p>Sign in with your assigned ID and password or PIN.</p>
              </div>

              <form onSubmit={submitManual} data-testid="login-form">
                <label>
                  Username / Employee ID
                  <Input
                    value={employeeId}
                    onChange={e => setEmployeeId(e.target.value)}
                    data-testid="login-employee-id-input"
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="e.g. Phone number, C01 or ADM01"
                    required
                  />
                </label>
                <label>
                  {remote ? "Password" : "PIN / Password"}
                  <Input
                    value={pin}
                    onChange={e => setPin(e.target.value)}
                    data-testid="login-pin-input"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    required
                  />
                  {remote && (
                    <span className="text-meta text-muted-foreground" data-testid="login-remote-note">
                      You are signing in from outside the restaurant. Use your password; a PIN works only at the restaurant.
                    </span>
                  )}
                </label>
                {error && (
                  <div className="form-error" data-testid="login-error-message">{error}</div>
                )}
                <Button
                  variant="action"
                  type="submit"
                  className="login-submit"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? "Checking access…" : "Enter RestoYaar"}
                  <ArrowRight size={18} />
                </Button>

                {!remote && (
                  <div className="pt-4 text-center">
                    <button
                      type="button"
                      onClick={() => { setMode("pin"); setError(""); setPin(""); }}
                      data-testid="switch-to-pin-login"
                      className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1.5"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Back to Quick PIN Sign-In
                    </button>
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
